import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Text from '../../elements/Typo/Text'
import { trackEvent } from '../../_common/functions'
import { RxCross2 } from 'react-icons/rx'
import { IoCheckmarkOutline, IoReloadOutline } from 'react-icons/io5'
import { RiEditLine, RiDeleteBinLine } from 'react-icons/ri'
import { useExperimentApi } from '../../store/api/hooks/experimentApiHook'
import toast from 'react-hot-toast'
import moment from 'moment'
import Loader from '../loader'
import CustomDatePicker from '../CustomDatePicker'
import { useDialog } from '../../store/api/hooks/dialogHook'
import ModalCloseIconButton from '../modalCloseIconButton'

interface EditRecurringModalProps {
    isModalOpen: boolean
    onClose: () => void
    experiment: any
}

const EditRecurringModal: React.FC<EditRecurringModalProps> = ({ isModalOpen, onClose, experiment }) => {
    const { t } = useTranslation()
    const { getExperimentDetailsWatch, updateRecurringDateConfig } = useExperimentApi()
    const [loading, setLoading] = useState<boolean>(false)
    const [saving, setSaving] = useState<boolean>(false)
    const [schedules, setSchedules] = useState<any[]>([])
    const [editingIndex, setEditingIndex] = useState<number | null>(null)
    const [editDate, setEditDate] = useState<Date | null>(null)
    const [recurringConfig, setRecurringConfig] = useState<any>(null)

    const [experimentData, setExperimentData] = useState<any>(null)

    const fetchExperimentRecurringDetails = () => {
        setLoading(true)
        getExperimentDetailsWatch(
            { literal: (experiment.schedule_parent_experiment ? experiment.experiment_literal : experiment.recurring_parent_literal) + '?use_orm=true' },
            (resp: any) => {
                setExperimentData(resp)

                //{recurringConfig.measurement_config.schedule_frequency_duration > 1 ? recurringConfig.measurement_config.schedule_frequency_duration : ''} {recurringConfig.measurement_config.schedule_frequency}
                const mConfig: any = resp.schedule_date_config.measurement_config
                let repeatEvery = ''
                if (mConfig.schedule_frequency.toLowerCase() == 'monthly') {
                    repeatEvery = mConfig.schedule_frequency_duration > 1 ? `${mConfig.schedule_frequency_duration} months` : '1 month'
                } else if (mConfig.schedule_frequency.toLowerCase() == 'weekly') {
                    repeatEvery = mConfig.schedule_frequency_duration > 1 ? `${mConfig.schedule_frequency_duration} weeks` : '1 week'
                }

                console.log('resp.schedule_date_config', resp)

                setRecurringConfig({ ...resp.schedule_date_config, measurement_config: { ...resp.schedule_date_config.measurement_config, repeat_every: repeatEvery } })
                setSchedules(resp.experiment_schedule_data ?? [])
                setLoading(false)
            },
            (error: any) => {
                trackEvent('recurring_edit_schedules_load_failed', { experiment_literal: experiment?.experiment_literal })
                toast.error(error?.message ?? t('Something went wrong'))
                setSchedules([])
                setLoading(false)
            },
        )
    }

    useEffect(() => {
        if (isModalOpen && experiment) {
            trackEvent('recurring_edit_modal_opened', { experiment_literal: experiment?.experiment_literal })
            fetchExperimentRecurringDetails()
        }
        if (!isModalOpen) {
            setSchedules([])
        }
    }, [isModalOpen, experiment])

    const handleStartEdit = (i: number, schedule: any) => {
        trackEvent('recurring_edit_date_started', { experiment_literal: experiment?.experiment_literal, schedule_literal: schedule?.schedule_literal })
        setEditingIndex(i)
        setEditDate(schedule.schedule_at ? new Date(schedule.schedule_at) : null)
    }

    const handleConfirmEdit = (i: number) => {
        if (editDate) {
            const newDate = moment(editDate).format('YYYY-MM-DDTHH:mm:ss')
            const schedule = schedules[i]
            trackEvent('recurring_edit_date_confirmed', { experiment_literal: experiment?.experiment_literal, schedule_literal: schedule?.schedule_literal, new_date: newDate })
            setSchedules((prev) => prev.map((s, index) => (index === i ? { ...s, schedule_at: newDate } : s)))
        }
        setEditingIndex(null)
        setEditDate(null)
    }

    const handleCancelEdit = () => {
        const schedule = editingIndex === null ? null : schedules[editingIndex]
        trackEvent('recurring_edit_date_cancelled', { experiment_literal: experiment?.experiment_literal, schedule_literal: schedule?.schedule_literal })
        setEditingIndex(null)
        setEditDate(null)
    }

    const handleDelete = (i: number) => {
        const schedule = schedules[i]
        const remaining = schedules.length - 1
        trackEvent('recurring_edit_schedule_deleted', { experiment_literal: experiment?.experiment_literal, schedule_literal: schedule?.schedule_literal, remaining_count: remaining })
        setSchedules((prev) => prev.filter((_, index) => index !== i))
    }

    const { confirm: confirmDialog } = useDialog()

    const getOrdinalSuffix = (n: number): string => {
        const v = n % 100
        if (v >= 11 && v <= 13) return 'th'
        switch (n % 10) {
            case 1:
                return 'st'
            case 2:
                return 'nd'
            case 3:
                return 'rd'
            default:
                return 'th'
        }
    }

    const handleUpdateWarning = () => {
        const originalDates = experimentData.experiment_schedule_data.map((s: any) => moment(s.schedule_at).format('YYYY-MM-DD'))
        const currentDates: any = schedules.map((s) => moment(s.schedule_at).format('YYYY-MM-DD'))
        const hasNoChange = originalDates.length === currentDates.length && originalDates.every((d: string, i: number) => d === currentDates[i])

        if (currentDates.length == 0) {
            toast.error(t('Please add at least one schedule date'))
            fetchExperimentRecurringDetails()
            return
        }

        if (hasNoChange) {
            trackEvent('recurring_edit_modal_closed', { experiment_literal: experiment?.experiment_literal })
            onClose()
        } else {
            confirmDialog({
                type: 'delete',
                title: t('Update Recurring Schedule'),
                message: t('Are you sure you want to save these changes to the recurring schedule?'),
                warning: t('Any deleted schedules cannot be restored. Changes will apply to upcoming occurrences.'),
                onConfirm: () => {
                    handleUpdate()
                },
                onCancel: () => {},
                confirmButtonLabel: t('Yes, Update'),
            })
        }
    }

    const handleUpdate = () => {
        trackEvent('recurring_edit_submitted', { experiment_literal: experiment?.experiment_literal, schedule_count: schedules.length })
        setSaving(true)
        const { measurement_config, design_config, ...rest } = recurringConfig
        const payload: any = {
            literal: experiment.schedule_parent_experiment ? experiment.experiment_literal : experiment.recurring_parent_literal,
            ...rest,
            measurement_change_config: measurement_config,
            design_change_config: design_config,
            users_schedule_at: schedules.map((s) => s.schedule_at).sort((a: any, b: any) => new Date(a).getTime() - new Date(b).getTime()),
        }

        updateRecurringDateConfig(
            payload,
            (resp: any) => {
                trackEvent('recurring_edit_success', { experiment_literal: experiment?.experiment_literal })
                toast.success(t('Saved Successfully'))
                setSaving(false)
                onClose()
            },
            (error: any) => {
                trackEvent('recurring_edit_failed', { experiment_literal: experiment?.experiment_literal, error_message: error?.message })
                toast.error(error?.message ?? t('Something went wrong'))
                setSaving(false)
            },
        )
    }

    const renderSchedulesContent = () => {
        if (loading) {
            return (
                <div className="flex justify-center py-8">
                    <Loader />
                </div>
            )
        }

        if (schedules.length === 0) {
            return (
                <div className="flex justify-center py-8">
                    <Text size="sm" className="text-gray-500">
                        {t('No Records Found')}
                    </Text>
                </div>
            )
        }

        return (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="grid grid-cols-10 bg-gray-50 border-b border-gray-200">
                    <div className="px-4 py-2 col-span-4">
                        <Text size="sm" className="font-semibold text-gray-600">
                            {t('Schedule')}
                        </Text>
                    </div>
                    <div className="px-4 py-2 col-span-4">
                        <Text size="sm" className="font-semibold text-gray-600">
                            {t('Date')}
                        </Text>
                    </div>
                    <div className="px-4 py-2 col-span-2">
                        <Text size="sm" className="font-semibold text-gray-600 pl-8">
                            {t('Action')}
                        </Text>
                    </div>
                </div>
                <div className="max-h-[20rem] overflow-auto">
                    {schedules.map((schedule: any, i: number) => (
                        <div key={schedule.schedule_literal} className="grid grid-cols-10 py-1 border-b border-gray-100 last:border-b-0 items-center">
                            <div className="px-4 col-span-4">
                                <Text size="sm" className="font-medium truncate" title={schedule.schedule_literal}>
                                    {schedule.schedule_literal}
                                </Text>
                            </div>
                            <div className="px-4 col-span-4">
                                {editingIndex === i ? (
                                    <CustomDatePicker
                                        size="sm"
                                        showClearIcon={false}
                                        value={editDate}
                                        minDate={moment().add(1, 'day').toDate()}
                                        maxDate={moment(recurringConfig.measurement_config.end_date).toDate()}
                                        onChange={(date) => setEditDate(date as Date)}
                                    />
                                ) : (
                                    <Text size="sm" className="text-gray-600">
                                        {schedule.schedule_at ? moment(schedule.schedule_at).format('DD MMM, YYYY') : '-'}
                                    </Text>
                                )}
                            </div>
                            <div className="px-4 col-span-2 flex items-center justify-center gap-2">
                                {editingIndex === i ? (
                                    <>
                                        <button type="button" className="w-7 h-7 rounded-md border border-gray-200 flex items-center justify-center text-green-500 hover:bg-green-50" onClick={() => handleConfirmEdit(i)}>
                                            <IoCheckmarkOutline size={14} />
                                        </button>
                                        <button type="button" className="w-7 h-7 rounded-md border border-gray-200 flex items-center justify-center text-red-500 hover:bg-red-50" onClick={handleCancelEdit}>
                                            <RxCross2 size={14} />
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button type="button" className="w-7 h-7 rounded-md border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-100" onClick={() => handleStartEdit(i, schedule)}>
                                            <RiEditLine size={14} />
                                        </button>
                                        <button type="button" className="w-7 h-7 rounded-md border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-red-50 hover:text-red-500" onClick={() => handleDelete(i)}>
                                            <RiDeleteBinLine size={14} />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    return (
        <dialog className={`modal ${isModalOpen ? 'modal-open' : ''}`}>
            <div className="modal-box remove-scroll border-0 p-3 rounded-[0.833rem] w-[50rem] flex flex-col relative font-sans">
                <ModalCloseIconButton
                    onClick={() => {
                        trackEvent('recurring_edit_modal_closed', { experiment_literal: experiment?.experiment_literal })
                        onClose()
                    }}
                />

                <Text className="!text-[1.2rem] font-bold text-center mb-4">{t('Recurring Schedule & Configuration')}</Text>

                {recurringConfig?.measurement_config && (
                    <div className="rounded-lg border border-slate-400 bg-slate-200 p-3">
                        <Text size="18" className="font-semibold text-slate-800 mb-2">
                            {t('Measurement Configuration')}
                        </Text>
                        <div className="text-[0.583rem] leading-[1.4]">
                            <div className="flex gap-2">
                                <span className="text-slate-900 font-medium w-[5.5rem]">{t('Recurring Period')}:</span>
                                <span className="">
                                    {moment(recurringConfig.measurement_config.start_date).format('DD MMM, YYYY')}&nbsp;&mdash;&nbsp;{moment(recurringConfig.measurement_config.end_date).format('DD MMM, YYYY')}{' '}
                                </span>
                            </div>
                            <div className="flex gap-2">
                                <span className="text-slate-900 font-medium w-[5.5rem]">{t('Repeat Every')}:</span>
                                <span className="capitalize">{recurringConfig.measurement_config.repeat_every ?? '-'}</span>
                            </div>
                            <div className="flex gap-2">
                                <span className="text-slate-900 font-medium w-[5.5rem]">{t('Selected Day / Date')}:</span>
                                <span className="">
                                    {recurringConfig.measurement_config.schedule_type_value_day_name
                                        ? recurringConfig.measurement_config.schedule_type_value_day_name
                                        : `${recurringConfig.measurement_config.schedule_type_value}${getOrdinalSuffix(Number(recurringConfig.measurement_config.schedule_type_value))} of every month`}
                                </span>
                            </div>
                            <div className="flex gap-2 items-center">
                                <span className="text-slate-900 font-medium w-[5.5rem]">{t('Measurement Type')}:</span>
                                <span className="capitalize">{recurringConfig.measurement_type}</span>
                                {recurringConfig.measurement_type === 'auto' && (
                                    <span className="bg-slate-500 text-white rounded-md pr-2 leading-none overflow-hidden flex items-center">
                                        <span className="bg-slate-700 px-1.5 mr-1.5 leading-[1.6]">{t('Table')}</span>
                                        {experimentData?.data_mappings?.delta_measurement_table_name}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-3 py-2">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <Text size="sm" className="font-semibold text-gray-600">
                                {t('Schedules')} ({schedules.length})
                            </Text>
                            <button
                                type="button"
                                className="w-6 h-6 rounded-md border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-100"
                                disabled={loading}
                                onClick={() => {
                                    trackEvent('recurring_edit_schedules_refreshed', { experiment_literal: experiment?.experiment_literal })
                                    fetchExperimentRecurringDetails()
                                }}
                            >
                                <IoReloadOutline size={12} />
                            </button>
                        </div>
                    </div>

                    {renderSchedulesContent()}

                    <div className="mt-6 flex items-center justify-center gap-3">
                        <button
                            type="button"
                            className="btn btn-sm btn-ghost gap-1"
                            onClick={() => {
                                trackEvent('recurring_edit_modal_closed', { experiment_literal: experiment?.experiment_literal })
                                onClose()
                            }}
                        >
                            <RxCross2 className="text-[1rem]" />
                            {t('Cancel')}
                        </button>
                        <button type="button" className="btn btn-sm btn-primary gap-1" disabled={saving || loading} onClick={handleUpdateWarning}>
                            {saving ? (
                                <Loader />
                            ) : (
                                <>
                                    <IoCheckmarkOutline className="text-[1rem]" /> {t('Submit')}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop pointer-events-none"></div>
        </dialog>
    )
}

export default EditRecurringModal
